import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useSpeechToText, { isSpeechRecognitionSupported } from './useSpeechToText';
import MicToggleButton from './MicToggleButton';
import './review.css';

/**
 * Plain-text comment composer (spec §Comments) — hidden until the caller
 * mounts it ("+ Comment" toggles rendering; there is no internal "hidden by
 * default" state here on purpose — not being mounted at all is the hidden
 * state, which is what the Jest tests assert via `queryByTestId`).
 *
 * - Save/Cancel; Esc cancels; empty-body Save is a client no-op (button
 *   disabled) — the backend also rejects empty bodies.
 * - Traps focus (Tab/Shift+Tab cycle within the composer) and always force-
 *   stops dictation on Save/Cancel/Esc/unmount (Security §8).
 * - Scrolls itself into view on mount (spec §Mobile: the composer must be the
 *   visible thing while composing) — harmless on desktop too.
 * - Forwards a ref to its root node so BreakdownTab can re-scroll it into
 *   view on a `visualViewport` resize (on-screen keyboard) while mobile.
 */
// Hoisted out of `forwardRef(function CommentComposer(...))`: the inner name matched the
// outer const, which is a shadow (#943, @typescript-eslint/no-shadow) and reads as one
// binding with two meanings. Declaring the render function first and wrapping it below
// keeps the displayName React DevTools shows, with no shadowing.
/**
 * The JSDoc has to sit on the render function, not on the `const` —
 * `forwardRef`'s props generic is inferred from the function it is handed, and
 * with nothing to infer from it defaults to `{}` (#968 Phase B).
 *
 * @param {{
 *   onSave: (body: string) => (void | Promise<void>),
 *   onCancel: () => void,
 *   placeholder?: string,
 *   initialValue?: string,
 *   testId?: string,
 * }} props
 * @param {React.ForwardedRef<HTMLElement>} forwardedRef
 */
function CommentComposerRender({
  onSave, onCancel,
  placeholder = 'Leave feedback — plain text, visible to the agent via get_review_feedback().',
  // #651: seeds the textarea for an EDIT (the raiser's existing comment body)
  // instead of always starting blank — the add/reply use is unaffected (default
  // ''). `testId` lets a caller mounting more than one composer at once (an
  // "Add comment" composer open alongside an in-place comment Edit, both real
  // states in CommentThread) give each a distinct data-testid instead of
  // colliding on the shared default.
  initialValue = '',
  testId = 'comment-composer',
}, forwardedRef) {
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);

  const setRefs = useCallback((node) => {
    containerRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;  
  }, [forwardedRef]);

  const insertAtCaret = useCallback((text) => {
    const el = textareaRef.current;
    setValue((prev) => {
      const start = el ? el.selectionStart : prev.length;
      const end = el ? el.selectionEnd : prev.length;
      const before = prev.slice(0, start);
      const after = prev.slice(end);
      const sep = before && !/\s$/.test(before) ? ' ' : '';
      return `${before}${sep}${text}${after}`;
    });
  }, []);

  const stt = useSpeechToText({ onTranscript: insertAtCaret });

  // Mount-only by design (see the "scrolls itself into view on mount" note above). The
  // empty array is genuinely exhaustive — the body touches only refs — so no suppression
  // is needed here; the directive that used to sit on it was suppressing nothing.
  useEffect(() => {
    textareaRef.current?.focus();
    containerRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, []);

  const doCancel = useCallback(() => {
    stt.stop();
    onCancel();
  }, [stt, onCancel]);

  const doSave = useCallback(async () => {
    const trimmed = value.trim();
    if (!trimmed) return; // empty-body Save no-ops
    stt.stop();
    setSaving(true);
    try {
      await onSave(trimmed);
      setValue('');
    } finally {
      setSaving(false);
    }
  }, [value, onSave, stt]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      doCancel();
      return;
    }
    if (e.key === 'Tab' && containerRef.current) {
      const focusables = Array.from(containerRef.current.querySelectorAll('button, textarea'))
        .filter((el) => !el.disabled);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    // Ported 1:1 from the mock's `.composer` (docs/design/specimen/review.html,
    // #450) — the accent border + glow signals "you're actively composing".
    // #793 H: `role="presentation"` — the div is a styling shell plus a FOCUS TRAP
    // (handleKeyDown cycles Tab between the first and last focusable child), so the
    // handler has to sit on the container, not on a child. It ADDS keyboard support
    // rather than withholding it, and the div has no semantics of its own to announce
    // — MUI's own Modal root carries this same role for this same reason.
    <div
      ref={setRefs}
      role="presentation"
      onKeyDown={handleKeyDown}
      data-testid={testId}
      className="composer"
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        rows={3}
        aria-label="Comment"
        data-testid={`${testId}-textarea`}
      />
      <div className="row">
        {isSpeechRecognitionSupported() && (
          <MicToggleButton listening={stt.listening} onToggle={stt.toggle} />
        )}
        {stt.listening && <span className="t-label" style={{ color: 'var(--error)' }}>Listening…</span>}
        <div className="grow" />
        <button type="button" className="btn sm ghost" onClick={doCancel} data-testid={`${testId}-cancel`}>Cancel</button>
        <button
          type="button"
          className="btn sm primary"
          onClick={doSave}
          disabled={!value.trim() || saving}
          data-testid={`${testId}-save`}
        >
          Save
        </button>
      </div>
    </div>
  );
}

const CommentComposer = forwardRef(CommentComposerRender);
CommentComposer.displayName = 'CommentComposer';

CommentComposer.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  initialValue: PropTypes.string,
  testId: PropTypes.string,
};

export default CommentComposer;
